import { useState, useEffect } from 'react';
import { useAuth } from '../context/AuthContext';
import { useTenantLocation } from '../context/LocationContext';
import { api } from '../lib/api';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Input } from '../components/ui/input';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../components/ui/table';
import { toast } from 'sonner';
import { RefreshCw, Search, Loader2, Users, ArrowRight, Mail, Phone, Award } from 'lucide-react';

export default function Customers() {
  const { token } = useAuth();
  const { currentLocationId } = useTenantLocation();
  const [customers, setCustomers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [syncing, setSyncing] = useState(false);
  const [syncStatusFilter, setSyncStatusFilter] = useState('all');
  const [searchTerm, setSearchTerm] = useState('');

  const fetchCustomers = async () => {
    setLoading(true);
    try {
      const params = {};
      if (syncStatusFilter !== 'all') params.sync_status = syncStatusFilter;
      const response = await api.getCustomers(token, params);
      setCustomers(response.data);
    } catch (error) {
      console.error('Failed to fetch customers:', error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchCustomers();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token, syncStatusFilter, currentLocationId]);

  const handleSync = async () => {
    setSyncing(true);
    try {
      const result = await api.syncCustomers(token);
      toast.success(result.data.message);
      fetchCustomers();
    } catch (error) {
      toast.error('Customer sync failed');
    } finally {
      setSyncing(false);
    }
  };

  const getSyncStatusBadge = (status) => {
    const styles = {
      synced: 'status-success',
      failed: 'status-error',
      pending: 'status-pending'
    };
    return styles[status] || 'status-pending';
  };

  const getTierBadge = (tier) => {
    const styles = {
      Bronze: 'bg-orange-500/15 text-orange-400 border border-orange-500/20',
      Silver: 'bg-slate-400/15 text-slate-300 border border-slate-400/20',
      Gold: 'bg-yellow-500/15 text-yellow-400 border border-yellow-500/20',
      Platinum: 'bg-purple-500/15 text-purple-400 border border-purple-500/20'
    };
    return styles[tier] || 'bg-slate-700 text-slate-400';
  };

  const filteredCustomers = customers.filter(customer => 
    customer.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
    customer.email?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    customer.phone?.includes(searchTerm)
  );

  return (
    <div className="space-y-6 animate-fade-in" data-testid="customers-page">
      {/* Header */}
      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
        <div>
          <h1 className="text-2xl md:text-3xl font-bold font-heading text-white tracking-tight">
            Customers
          </h1>
          <p className="text-slate-400 mt-1">Sync customers from UnTill to Odoo with deduplication</p>
        </div>
        <Button 
          onClick={handleSync}
          disabled={syncing}
          className="bg-blue-500 hover:bg-blue-600 text-white"
          data-testid="sync-customers-btn"
        >
          {syncing ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <RefreshCw className="w-4 h-4 mr-2" />}
          Sync Customers
        </Button>
      </div>

      {/* Sync Direction Indicator */}
      <Card className="bg-slate-900/50 border-slate-800">
        <CardContent className="p-4">
          <div className="flex items-center justify-center gap-4">
            <div className="flex items-center gap-2 px-4 py-2 rounded-lg bg-blue-500/10 border border-blue-500/20">
              <span className="text-blue-400 font-medium">UnTill POS</span>
              <span className="text-xs text-blue-500/60">(Source)</span>
            </div>
            <ArrowRight className="w-5 h-5 text-slate-500" />
            <div className="flex items-center gap-2 px-4 py-2 rounded-lg bg-emerald-500/10 border border-emerald-500/20">
              <span className="text-emerald-400 font-medium">Odoo</span>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Stats */}
      <div className="grid grid-cols-1 sm:grid-cols-4 gap-4">
        <Card className="bg-slate-900/50 border-slate-800">
          <CardContent className="p-4 text-center">
            <p className="text-2xl font-bold text-white">{customers.length}</p>
            <p className="text-slate-400 text-sm">Total Customers</p>
          </CardContent>
        </Card>
        <Card className="bg-slate-900/50 border-slate-800">
          <CardContent className="p-4 text-center">
            <p className="text-2xl font-bold text-emerald-400">{customers.filter(c => c.sync_status === 'synced').length}</p>
            <p className="text-slate-400 text-sm">Synced</p>
          </CardContent>
        </Card>
        <Card className="bg-slate-900/50 border-slate-800">
          <CardContent className="p-4 text-center">
            <p className="text-2xl font-bold text-amber-400">{customers.filter(c => c.membership_tier).length}</p>
            <p className="text-slate-400 text-sm">With Membership</p>
          </CardContent>
        </Card>
        <Card className="bg-slate-900/50 border-slate-800">
          <CardContent className="p-4 text-center">
            <p className="text-2xl font-bold text-purple-400">
              {customers.reduce((sum, c) => sum + c.loyalty_points, 0).toLocaleString()}
            </p>
            <p className="text-slate-400 text-sm">Total Loyalty Points</p>
          </CardContent>
        </Card>
      </div>

      {/* Filters */}
      <Card className="bg-slate-900/50 border-slate-800">
        <CardContent className="p-4">
          <div className="flex flex-col md:flex-row gap-4">
            <div className="relative flex-1">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-500" />
              <Input
                placeholder="Search by name, email, or phone..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="pl-10 bg-slate-800/50 border-slate-700 text-white"
                data-testid="customers-search-input"
              />
            </div>
            <Select value={syncStatusFilter} onValueChange={setSyncStatusFilter}>
              <SelectTrigger className="w-[180px] bg-slate-800/50 border-slate-700 text-white" data-testid="sync-status-filter">
                <SelectValue placeholder="Sync Status" />
              </SelectTrigger>
              <SelectContent className="bg-slate-900 border-slate-800">
                <SelectItem value="all">All Status</SelectItem>
                <SelectItem value="synced">Synced</SelectItem>
                <SelectItem value="pending">Pending</SelectItem>
                <SelectItem value="failed">Failed</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </CardContent>
      </Card>

      {/* Customers Table */}
      <Card className="bg-slate-900/50 border-slate-800">
        <CardContent className="p-0">
          {loading ? (
            <div className="flex items-center justify-center py-12">
              <Loader2 className="w-6 h-6 animate-spin text-blue-500" />
            </div>
          ) : filteredCustomers.length === 0 ? (
            <div className="text-center py-12 text-slate-500">
              No customers found. Seed demo data from the dashboard.
            </div>
          ) : (
            <Table>
              <TableHeader>
                <TableRow className="border-slate-800 hover:bg-transparent">
                  <TableHead className="text-slate-400">Name</TableHead>
                  <TableHead className="text-slate-400">Contact</TableHead>
                  <TableHead className="text-slate-400">Membership</TableHead>
                  <TableHead className="text-slate-400">Loyalty Points</TableHead>
                  <TableHead className="text-slate-400">Odoo ID</TableHead>
                  <TableHead className="text-slate-400">Sync Status</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {filteredCustomers.map((customer) => (
                  <TableRow key={customer.id} className="border-slate-800 data-table-row">
                    <TableCell className="text-white font-medium">{customer.name}</TableCell>
                    <TableCell>
                      <div className="space-y-1">
                        {customer.email && (
                          <div className="flex items-center gap-2 text-slate-400 text-sm">
                            <Mail className="w-3 h-3" />
                            {customer.email}
                          </div>
                        )}
                        {customer.phone && (
                          <div className="flex items-center gap-2 text-slate-400 text-sm">
                            <Phone className="w-3 h-3" />
                            {customer.phone}
                          </div>
                        )}
                      </div>
                    </TableCell>
                    <TableCell>
                      {customer.membership_tier ? (
                        <Badge className={getTierBadge(customer.membership_tier)}>
                          <Award className="w-3 h-3 mr-1" />
                          {customer.membership_tier}
                        </Badge>
                      ) : (
                        <span className="text-slate-500 text-sm">None</span>
                      )}
                    </TableCell>
                    <TableCell className="text-white font-mono">
                      {customer.loyalty_points.toLocaleString()}
                    </TableCell>
                    <TableCell className="font-mono text-slate-400 text-sm">
                      {customer.odoo_id}
                    </TableCell>
                    <TableCell>
                      <Badge className={getSyncStatusBadge(customer.sync_status)}>{customer.sync_status}</Badge>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
